import React, { useState } from 'react';
import { Search, Plus, Users, Star, CheckCheck } from 'lucide-react';

const ContactsSidebar = ({
  conversations = [],
  contacts = [],
  searchQuery = '',
  setSearchQuery,
  selectedConversation,
  onSelectConversation,
  onStartConversation,
  unreadCounts = {},
  onlineUsers = new Set(),
  getRoleIcon,
  formatTime
}) => {
  const [activeTab, setActiveTab] = useState('conversations');

  const getConversationName = (conversation) => {
    if (conversation?.name) return conversation?.name;
    const participant = conversation?.participants?.[0];
    return participant?.user_profiles?.full_name || 'Conversation';
  };

  const getConversationSubtext = (conversation) => {
    const participant = conversation?.participants?.[0];
    return participant?.user_profiles?.partners?.[0]?.business_name || 
           participant?.user_profiles?.role || 'Utilisateur';
  };

  const categorizeContacts = () => {
    const categories = {
      agents: contacts?.filter(c => c?.role === 'agent' || c?.partners?.[0]?.partner_type === 'agent') || [],
      venues: contacts?.filter(c => c?.role === 'venue' || c?.partners?.[0]?.partner_type === 'venue') || [],
      bookers: contacts?.filter(c => c?.role === 'booker' || c?.partners?.[0]?.partner_type === 'booker') || [],
      others: contacts?.filter(c => !['agent', 'venue', 'booker']?.includes(c?.role || c?.partners?.[0]?.partner_type)) || []
    };
    return categories;
  };

  const categorizedContacts = categorizeContacts();

  return (
    <div className="w-80 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 flex flex-col transition-colors">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-neutral-800">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Messages</h1>
          <button className="p-2 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
            <Plus className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-neutral-500" />
          <input
            type="text"
            placeholder="Rechercher des contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery?.(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          />
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-neutral-800">
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'conversations' ?'text-blue-600 border-b-2 border-blue-600 dark:border-blue-500' :'text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200'
          }`}
        >
          Conversations
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'contacts' ?'text-blue-600 border-b-2 border-blue-600 dark:border-blue-500' :'text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200'
          }`}
        >
          Contacts
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'conversations' ? (
          <div className="p-2">
            {conversations?.length > 0 ? (
              conversations?.map((conversation) => (
                <button
                  key={conversation?.id}
                  onClick={() => onSelectConversation?.(conversation)}
                  className={`w-full p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors mb-1 ${
                    selectedConversation?.id === conversation?.id ? 'bg-blue-50 dark:bg-blue-500/15 border-l-4 border-blue-500 dark:border-blue-400' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <img
                        src={conversation?.participants?.[0]?.user_profiles?.avatar_url || `/api/placeholder/40/40`}
                        alt=""
                        className="w-10 h-10 rounded-full"
                      />
                      {onlineUsers?.has(conversation?.participants?.[0]?.user_profiles?.id) && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white dark:border-neutral-900 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                          {getConversationName(conversation)}
                        </p>
                        <div className="flex items-center space-x-1">
                          {unreadCounts?.[conversation?.id] > 0 && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[18px] text-center shadow-sm">
                              {unreadCounts?.[conversation?.id]}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 dark:text-neutral-400">
                            {formatTime?.(conversation?.updated_at)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                        {getConversationSubtext(conversation)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-neutral-500 text-sm">Aucune conversation</p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-2">
            {/* Agents Section */}
            {categorizedContacts?.agents?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-2 px-2 flex items-center">
                  🎵 Agents ({categorizedContacts?.agents?.length})
                </h3>
                {categorizedContacts?.agents?.map((contact) => (
                  <button
                    key={contact?.id}
                    onClick={() => onStartConversation?.(contact)}
                    className="w-full p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors mb-1"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={contact?.avatar_url || `/api/placeholder/40/40`}
                          alt=""
                          className="w-10 h-10 rounded-full"
                        />
                        {contact?.partners?.[0]?.is_verified && (
                          <CheckCheck className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-blue-500 drop-shadow" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                            {contact?.full_name}
                          </p>
                          {contact?.partners?.[0]?.average_rating > 0 && (
                            <div className="flex items-center">
                              <Star className="w-3 h-3 text-yellow-400 fill-current" />
                              <span className="text-xs text-gray-500 dark:text-neutral-400 ml-1">
                                {contact?.partners?.[0]?.average_rating}
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                          {contact?.partners?.[0]?.business_name || 'Agent musical'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Venues Section */}
            {categorizedContacts?.venues?.length > 0 && (
              <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-2 px-2 flex items-center">
                  🏛️ Lieux ({categorizedContacts?.venues?.length})
                </h3>
                {categorizedContacts?.venues?.map((contact) => (
                  <button
                    key={contact?.id}
                    onClick={() => onStartConversation?.(contact)}
          className="w-full p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors mb-1"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={contact?.avatar_url || `/api/placeholder/40/40`}
                          alt=""
                          className="w-10 h-10 rounded-full"
                        />
                        {contact?.partners?.[0]?.is_verified && (
                          <CheckCheck className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                            {contact?.full_name}
                          </p>
                          {contact?.partners?.[0]?.average_rating > 0 && (
                            <div className="flex items-center">
                              <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-xs text-gray-500 dark:text-neutral-400 ml-1">
                                {contact?.partners?.[0]?.average_rating}
                              </span>
                            </div>
                          )}
                        </div>
            <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                          {contact?.partners?.[0]?.business_name || 'Lieu de spectacle'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Bookers/Influencers Section */}
            {categorizedContacts?.bookers?.length > 0 && (
              <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-2 px-2 flex items-center">
                  📅 Bookers & Influenceurs ({categorizedContacts?.bookers?.length})
                </h3>
                {categorizedContacts?.bookers?.map((contact) => (
                  <button
                    key={contact?.id}
                    onClick={() => onStartConversation?.(contact)}
          className="w-full p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors mb-1"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={contact?.avatar_url || `/api/placeholder/40/40`}
                          alt=""
                          className="w-10 h-10 rounded-full"
                        />
                        {contact?.partners?.[0]?.is_verified && (
                          <CheckCheck className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                            {contact?.full_name}
                          </p>
                          {contact?.partners?.[0]?.average_rating > 0 && (
                            <div className="flex items-center">
                              <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-xs text-gray-500 dark:text-neutral-400 ml-1">
                                {contact?.partners?.[0]?.average_rating}
                              </span>
                            </div>
                          )}
                        </div>
            <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                          {contact?.partners?.[0]?.business_name || 'Booker professionnel'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Others Section */}
            {categorizedContacts?.others?.length > 0 && (
              <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-2 px-2 flex items-center">
                  👤 Autres ({categorizedContacts?.others?.length})
                </h3>
                {categorizedContacts?.others?.map((contact) => (
                  <button
                    key={contact?.id}
                    onClick={() => onStartConversation?.(contact)}
          className="w-full p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors mb-1"
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={contact?.avatar_url || `/api/placeholder/40/40`}
                        alt=""
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                          {contact?.full_name}
                        </p>
            <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                          {contact?.role || 'Utilisateur'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {contacts?.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-neutral-500 text-sm">Aucun contact trouvé</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsSidebar;